import type {
  BusinessType,
  IdRequirement,
  IdentificationType,
} from 'typesgen/zod.gen';

export type { BusinessType, IdRequirement, IdentificationType };

/** Map from document type to its uploaded file. */
type DocumentUploads = Partial<Record<IdentificationType, File>>;

export interface PersonalInfoFormState {
  legalFullName: string;
  dateOfBirth: string; // MM/DD/YYYY
  country: string; // ISO 3166-1 alpha-2
  address: string;
  errors: {
    legalFullName?: string;
    dateOfBirth?: string;
    country?: string;
    address?: string;
  };
}

export interface IdentityDocsFormState {
  documentUploads: DocumentUploads;
  errors: {
    documents?: string;
  };
}

export interface BusinessInfoFormState {
  businessName: string;
  businessType: BusinessType | '';
  documentUploads: DocumentUploads;
  errors: {
    businessName?: string;
    businessType?: string;
    documents?: string;
  };
}

export const ID_TYPE_LABELS: Record<IdentificationType, string> = {
  DRIVERS_LICENSE: "Driver's License",
  PASSPORT: 'Passport',
  NATIONAL_ID: 'National ID',
  NATIONAL_ID_NON_CITIZEN: 'National ID (Non-Citizen)',
  GHANA_CARD: 'Ghana Card',
  RWANDA_CARD: 'Rwanda Card',
  ECOWAS_ID: 'ECOWAS ID',
  TRAVEL_DOCUMENT: 'Travel Document',
  SSNIT: 'SSNIT Card',
  SSN: 'Social Security Number',
  VOTER_ID: "Voter's ID",
  RESIDENCE_PERMIT: 'Residence Permit',
  TAX_IDENTIFICATION_NUMBER: 'Tax Identification Number',
  BUSINESS_REGISTRATION_DOCUMENTS: 'Business Registration Documents',
};

export const BUSINESS_TYPE_LABELS: Record<BusinessType, string> = {
  SOLE_PROPRIETORSHIP: 'Sole Proprietorship',
  PARTNERSHIP: 'Partnership',
  CORPORATION: 'Corporation',
  NON_PROFIT: 'Non-Profit',
  LLC: 'LLC',
  OTHER: 'Other',
};

/**
 * Check whether all requirement groups are satisfied by the given uploads.
 * - ANY_OF: at least one type in the group has a file
 * - ALL_OF: every type in the group has a file
 */
export const areRequirementsMet = (
  requirements: IdRequirement[],
  uploads: DocumentUploads,
): boolean => {
  return requirements.every((req) => {
    const types = req.idTypes ?? [];
    if (types.length === 0) {
      return true;
    }
    if (req.condition === 'ANY_OF') {
      return types.some((t) => uploads[t] != null);
    }
    return types.every((t) => uploads[t] != null);
  });
};

export const emptyPersonalInfoFormState = (): PersonalInfoFormState => ({
  legalFullName: '',
  dateOfBirth: '',
  country: '',
  address: '',
  errors: {},
});

export const emptyIdentityDocsFormState = (): IdentityDocsFormState => ({
  documentUploads: {},
  errors: {},
});

export const emptyBusinessInfoFormState = (): BusinessInfoFormState => ({
  businessName: '',
  businessType: '',
  documentUploads: {},
  errors: {},
});
